import * as React from 'react';
import { toast as sonnerToast } from 'sonner';

export type ToastAction = {
  label: React.ReactNode;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

export type ToastInput = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: 'default' | 'destructive' | 'warning';
  action?: ToastAction;
};

type ToastReturn = {
  id: string | number;
  dismiss: () => void;
  update: (props: ToastInput) => void;
};

function toMessage(title?: React.ReactNode): string {
  if (title == null || title === '') return '';
  if (typeof title === 'string' || typeof title === 'number') return String(title);
  return '';
}

function toDescription(description?: React.ReactNode): string | undefined {
  if (description == null || description === '') return undefined;
  if (typeof description === 'string' || typeof description === 'number') return String(description);
  return undefined;
}

function toast({ title, description, variant, action }: ToastInput): ToastReturn {
  const message = toMessage(title);
  const desc = toDescription(description);
  const options = { description: desc, action };

  const id =
    variant === 'destructive'
      ? sonnerToast.error(message, { ...options, duration: 10000, closeButton: true })
      : variant === 'warning'
        ? sonnerToast.warning(message, options)
        : sonnerToast(message, options);

  return {
    id,
    dismiss: () => sonnerToast.dismiss(id),
    update: (props: ToastInput) => {
      sonnerToast.dismiss(id);
      toast(props);
    },
  };
}

function useToast() {
  return {
    toasts: [] as never[],
    toast,
    dismiss: (toastId?: string | number) => sonnerToast.dismiss(toastId),
  };
}

export { useToast, toast };
